'use client';

import { forwardRef, type ReactNode } from 'react';
import { InlineTagChip, type InlineTagChipProps } from '../InlineTagChip';

export interface CitationChipProps extends Omit<InlineTagChipProps, 'icon' | 'label'> {
  /** Citation key (e.g. "policy_analysis", "credit_pull", "bank_statements:p3"). */
  citationKey: string;
  /**
   * Optional source detail rendered in the tooltip (e.g. document title, retrieval timestamp).
   * If not provided, the citationKey itself is shown.
   */
  source?: ReactNode;
}

/**
 * Small inline citation tag embedded in body text, anchored to a source. Clicking typically
 * scrolls / focuses the matching entry in a citations panel.
 *
 * For tool-execution references, prefer `ToolCallChip` instead.
 */
export const CitationChip = forwardRef<HTMLElement, CitationChipProps>(function CitationChip(
  { citationKey, source, severity = 'success', tooltip, ariaLabel, ...rest },
  ref,
) {
  return (
    <InlineTagChip
      ref={ref}
      label={citationKey}
      severity={severity}
      tooltip={tooltip ?? source ?? citationKey}
      ariaLabel={ariaLabel ?? `Citation: ${citationKey}`}
      {...rest}
    />
  );
});

CitationChip.displayName = 'CitationChip';

export default CitationChip;
